import React, {useEffect, useRef, useState} from "react";
import PropTypes from 'prop-types';

import {dataManager} from "../managers/DataManager";
import {flowManager} from "../managers/FlowManager";
import {userManager} from "../managers/UserManager";
import {StepHeader} from "../components/StepHeader";
import {StepCategory} from "../components/StepCategory";
import {TileCheckbox} from "../components/TileCheckbox";
import {SubCategoryTile} from "../components/SubCategoryTile";
import {dataKeys} from "../enums/dataKeys";

HomeyCloudCategoryStep.propTypes = {
  index: PropTypes.number
}

export function HomeyCloudCategoryStep(props) {
  const name = HomeyCloudCategoryStep.name;
  const data = dataManager.store(state => state.data); // Data is updated on state change.
  const steps = flowManager.store(state => state.steps); // Steps is updated on state change.
  const stepRef = useRef(null);
  const [checkValue, setCheckValue] = useState("");
  const homey = userManager.getSelectedHomey();
  const options = {};

  // On mount, focus the step and scroll into view.
  useEffect(() => {
    stepRef.current.focus({ preventScroll: true });
    window.scrollTo({
      top: (stepRef.current.offsetTop - 120),
      behavior: "smooth"
    });
  }, [])

  useEffect(() => {
    const entry = data.find(item => item.name === name);
    if (entry) {
      setCheckValue(entry.value);
    }
  }, [data, name])

  function handleSelect(event) {
    const value = event.target.value;
    const data = options[value].data;
    const action = data.action;
    const entry = { name, value, ...data };

    flowManager.nextActionStep(props.index, entry, action);
  }

  // Add-ons
  options[`addOns-0`] = {
    title: "Homey Bridge",
    icon: "homey-bridge",
    data: { ...HomeyCloudCategoryStep.homeyBridge }
  };
  options[`addOns-1`] = {
    title: "Homey Portal",
    icon: "homey-portal",
    data: { ...HomeyCloudCategoryStep.homeyPortal }
  };
  options[`addOns-2`] = {
    title: "Homey Energy Dongle",
    icon: "energy-dongle",
    data: { ...HomeyCloudCategoryStep.homeyEnergyDongle }
  };

  // Apps & Devices
  options[`appsAndDevices-0`] = {
    title: "Apps",
    icon: "apps",
    data: { ...HomeyCloudCategoryStep.apps }
  };
  options[`appsAndDevices-1`] = {
    title: "Devices",
    icon: "devices",
    data: { ...HomeyCloudCategoryStep.devices }
  };

  // Automation
  options[`automation-0`] = {
    title: "Flow",
    icon: "flow",
    data: { ...HomeyCloudCategoryStep.flow }
  };
  options[`automation-1`] = {
    title: "Logic",
    icon: "logic",
    data: { ...HomeyCloudCategoryStep.logic }
  };

  // Monitoring
  options[`monitoring-0`] = {
    title: "Insights",
    icon: "insights",
    data: { ...HomeyCloudCategoryStep.insights }
  };
  options[`monitoring-1`] = {
    title: "Energy",
    icon: "energy",
    data: { ...HomeyCloudCategoryStep.energy }
  };
  options[`monitoring-2`] = {
    title: "Dashboards",
    icon: "dashboards",
    data: { ...HomeyCloudCategoryStep.dashboards }
  };

  // Platform
  options['platform-0'] = {
    title: "Mobile App",
    icon: "phone",
    data: { ...HomeyCloudCategoryStep.mobileApp }
  };
  options['platform-1'] = {
    title: "Web App",
    icon: "webapp",
    data: { ...HomeyCloudCategoryStep.webApp }
  };

  // Voice Assistants
  options[`voiceAssistants-0`] = {
    title: "Amazon Alexa",
    icon: "alexa",
    data: { ...HomeyCloudCategoryStep.alexa }
  };
  options[`voiceAssistants-1`] = {
    title: "Google Assistant",
    icon: "google",
    data: { ...HomeyCloudCategoryStep.google }
  };
  options[`voiceAssistants-2`] = {
    title: "Siri",
    icon: "siri",
    data: { ...HomeyCloudCategoryStep.siri }
  };

  // Warranty
  options[`warranty-0`] = {
    title: "Defects",
    icon: "defects",
    data: { ...HomeyCloudCategoryStep.defects }
  };
  options[`warranty-1`] = {
    title: "Returns",
    icon: "returns",
    data: { ...HomeyCloudCategoryStep.returns }
  };

  // Wireless Technologies
  if (homey.bridges.length > 0) {
    options[`wireless-0`] = {
      title: "433 MHz",
      icon: "433mhz",
      data: { ...HomeyCloudCategoryStep["433mhz"] }
    };
    options[`wireless-2`] = {
      title: "Bluetooth",
      icon: "bluetooth",
      data: { ...HomeyCloudCategoryStep.bluetooth }
    };
    options[`wireless-3`] = {
      title: "Infrared",
      icon: "infrared",
      data: { ...HomeyCloudCategoryStep.infrared }
    };
  }
  options[`wireless-4`] = {
    title: "Wi-Fi",
    icon: "wifi",
    data: { ...HomeyCloudCategoryStep.wifi }
  };
  if (homey.bridges.length > 0) {
    options[`wireless-5`] = {
      title: "Zigbee",
      icon: "zigbee",
      data: { ...HomeyCloudCategoryStep.zigbee }
    };
    options[`wireless-6`] = {
      title: "Z-Wave",
      icon: "z-wave",
      data: { ...HomeyCloudCategoryStep.zwave }
    };
  }

  // More
  options[`more-0`] = {
    title: "Family",
    icon: "family",
    data: { ...HomeyCloudCategoryStep.family }
  };
  options[`more-1`] = {
    title: "Other",
    icon: "other",
    data: { ...HomeyCloudCategoryStep.other }
  };

  return (
    <>
      <div ref={stepRef} tabIndex={0} className="support-form-step">
        <StepHeader
          index={props.index}
          question="How can we help you with Homey Cloud?"
          description="Choose the category that describes your question or problem best."
        />
        <div className="support-form-step__categories">
          <StepCategory heading="Add-ons">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("addOns") && (
                  <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                    <SubCategoryTile title={option.title} icon={option.icon}/>
                  </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory heading="Apps & Devices">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("appsAndDevices") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory heading="Automation">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("automation") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory heading="Monitoring">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("monitoring") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory heading="Platform">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("platform") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory heading="Voice Assistants">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("voiceAssistants") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory heading="Warranty">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("warranty") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory heading="Wireless Technologies">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("wireless") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory heading="More">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("more") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
        </div>
      </div>
      {steps.length === props.index + 1 && (
        <div className="support-form-step">
          <StepHeader
            index={props.index + 1}
            question="Select one of the above options."
          />
        </div>
      )}
    </>
  );
}

HomeyCloudCategoryStep.apps = {
  action: "apps",
  [dataKeys.labels]: ["apps"],
  [dataKeys.path]: ["Apps"]
}
HomeyCloudCategoryStep.devices = {
  action: "devices",
  [dataKeys.labels]: ["devices"],
  [dataKeys.path]: ["Devices"]
}
HomeyCloudCategoryStep.flow = {
  action: "flow",
  [dataKeys.labels]: ["flow"],
  [dataKeys.path]: ["Flow"]
}
HomeyCloudCategoryStep.logic = {
  action: "logic",
  [dataKeys.labels]: ["logic"],
  [dataKeys.path]: ["Logic"]
}
HomeyCloudCategoryStep.insights = {
  action: "insights",
  [dataKeys.labels]: ["insights"],
  [dataKeys.path]: ["Insights"]
}
HomeyCloudCategoryStep.energy = {
  action: "energy",
  [dataKeys.labels]: ["energy"],
  [dataKeys.path]: ["Energy"]
}
HomeyCloudCategoryStep.dashboards = {
  action: "dashboards",
  [dataKeys.labels]: ["dashboards"],
  [dataKeys.path]: ["Dashboards"]
}
HomeyCloudCategoryStep.mobileApp = {
  action: "mobileApp",
  [dataKeys.labels]: ["mobile-app"],
  [dataKeys.path]: ["Mobile App"]
}
HomeyCloudCategoryStep.webApp = {
  action: "webApp",
  [dataKeys.labels]: ["web-app"],
  [dataKeys.path]: ["Web App"]
}
HomeyCloudCategoryStep.alexa = {
  action: "alexa",
  [dataKeys.labels]: ["alexa"],
  [dataKeys.path]: ["Amazon Alexa"]
}
HomeyCloudCategoryStep.google = {
  action: "google",
  [dataKeys.labels]: ["google-assistant"],
  [dataKeys.path]: ["Google Assistant"]
}
HomeyCloudCategoryStep.siri = {
  action: "siri",
  [dataKeys.labels]: ["siri"],
  [dataKeys.path]: ["Siri"]
}
HomeyCloudCategoryStep.defects = {
  action: "defects",
  [dataKeys.labels]: ["defects"],
  [dataKeys.path]: ["Defects"]
}
HomeyCloudCategoryStep.returns = {
  action: "returns",
  [dataKeys.labels]: ["returns"],
  [dataKeys.path]: ["Returns"]
}
HomeyCloudCategoryStep.wifi = {
  action: "wifi",
  [dataKeys.labels]: ["wifi"],
  [dataKeys.path]: ["Wi-Fi"]
}
HomeyCloudCategoryStep.family = {
  action: "family",
  [dataKeys.labels]: ["family"],
  [dataKeys.path]: ["Family"]
}
HomeyCloudCategoryStep.homeyBridge = {
  action: "homeyBridge",
  [dataKeys.labels]: ["add-ons", "homey-bridge"],
  [dataKeys.path]: ["Add-ons", "Homey Bridge"]
}
HomeyCloudCategoryStep.homeyPortal = {
  action: "homeyPortal",
  [dataKeys.labels]: ["add-ons", "homey-portal"],
  [dataKeys.path]: ["Add-ons", "Homey Portal"]
}
HomeyCloudCategoryStep.homeyEnergyDongle = {
  action: "homeyEnergyDongle",
  [dataKeys.labels]: ["add-ons", "homey-energy-dongle"],
  [dataKeys.path]: ["Add-ons", "Homey Energy Dongle"]
}
HomeyCloudCategoryStep.other = {
  action: "other",
  [dataKeys.labels]: ["other"],
  [dataKeys.path]: ["Other"]
}

HomeyCloudCategoryStep["433mhz"] = {
  action: "433mhz",
  [dataKeys.labels]: ["homey-bridge", "433mhz"],
  [dataKeys.path]: ["433 MHz"]
}
HomeyCloudCategoryStep.bluetooth = {
  action: "bluetooth",
  [dataKeys.labels]: ["homey-bridge", "bluetooth"],
  [dataKeys.path]: ["Bluetooth"]
}
HomeyCloudCategoryStep.infrared = {
  action: "infrared",
  [dataKeys.labels]: ["homey-bridge", "infrared"],
  [dataKeys.path]: ["Infrared"]
}
HomeyCloudCategoryStep.zigbee = {
  action: "zigbee",
  [dataKeys.labels]: ["homey-bridge", "zigbee"],
  [dataKeys.path]: ["Zigbee"]
}
HomeyCloudCategoryStep.zwave = {
  action: "zwave",
  [dataKeys.labels]: ["homey-bridge", "z-wave"],
  [dataKeys.path]: ["Z-Wave"]
}
